import type { SxProps, Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';

export const iconSx: SxProps<Theme> = {
  fontSize: '0.875rem',
  width: '0.875rem',
  height: '0.875rem',
  lineHeight: 1,
  flexShrink: 0,
};

export const labelSx: SxProps<Theme> = {
  fontSize: '0.78125rem',
  fontWeight: 600,
  color: (theme) => theme.palette.custom.OV.sys.onSurface,
  minWidth: 0,
};

export const dismissedChipSx: SxProps<Theme> = {
  minHeight: '1.625rem',
  minWidth: 0,
  opacity: 0.6,
};

export const successIconSx: SxProps<Theme> = (theme) => ({
  fontSize: '0.875rem',
  width: '0.875rem',
  height: '0.875rem',
  lineHeight: 1,
  flexShrink: 0,
  color: theme.palette.custom.OV.sys.success,
});

export const chipContentSx: SxProps<Theme> = { minWidth: 0, pr: 0.875 };

export const suggestedChipRootSx =
  (isResolving: boolean): SxProps<Theme> =>
  (theme) => ({
    minHeight: '1.625rem',
    minWidth: 0,
    maxWidth: '100%',
    pl: 1.25,
    pr: 0.375,
    borderRadius: '999px',
    border: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
    backgroundColor: theme.palette.custom.OV.sys.surfaceContainer,
    opacity: isResolving ? 0.6 : 1,
  });

export const suggestedChipActionsSx: SxProps<Theme> = (theme) => ({
  flexShrink: 0,
  pl: 0.375,
  borderLeft: `1px solid ${alpha(theme.palette.custom.OV.sys.outlineVariant, 0.7)}`,
});

export const acceptButtonSx: SxProps<Theme> = (theme) => ({
  width: '1.25rem',
  height: '1.25rem',
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.18),
  },
});

export const dismissButtonSx: SxProps<Theme> = (theme) => ({
  width: '1.25rem',
  height: '1.25rem',
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  '&:hover': {
    color: theme.palette.custom.OV.sys.onSurface,
    backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHighest,
  },
});

export const failedChipRootSx: SxProps<Theme> = (theme) => ({
  minHeight: '1.625rem',
  minWidth: 0,
  maxWidth: '100%',
  pl: 1.25,
  pr: 0.375,
  borderRadius: '999px',
  border: `1px solid ${alpha(theme.palette.error.main, 0.42)}`,
  backgroundColor: alpha(theme.palette.error.main, 0.08),
});

export const failedIconSx: SxProps<Theme> = (theme) => ({
  fontSize: '0.875rem',
  width: '0.875rem',
  height: '0.875rem',
  lineHeight: 1,
  flexShrink: 0,
  color: theme.palette.error.main,
});

export const failedLabelSx: SxProps<Theme> = (theme) => ({
  fontSize: '0.625rem',
  fontWeight: 700,
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
  color: theme.palette.error.main,
  minWidth: 0,
});

export const failedChipActionsSx: SxProps<Theme> = (theme) => ({
  flexShrink: 0,
  pl: 0.375,
  borderLeft: `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
});

export const retryHistorySx: SxProps<Theme> = (theme) => ({
  flexShrink: 0,
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  cursor: 'default',
});

export const retryButtonSx: SxProps<Theme> = (theme) => ({
  height: '1.25rem',
  px: 0.75,
  gap: 0.5,
  borderRadius: '999px',
  color: theme.palette.error.main,
  fontSize: '0.6875rem',
  fontWeight: 700,
  '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.14) },
});

export const reasonTooltipSx: SxProps<Theme> = { maxWidth: '20rem' };

export const reasonCursorSx: SxProps<Theme> = { cursor: 'default' };

export const reasonHeaderIconSx: SxProps<Theme> = (theme) => ({
  fontSize: '0.875rem',
  width: '0.875rem',
  height: '0.875rem',
  lineHeight: 1,
  flexShrink: 0,
  color: theme.palette.primary.main,
});

export const reasonHeaderSx: SxProps<Theme> = (theme) => ({
  fontSize: '0.65625rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: theme.palette.primary.main,
});

export const reasonBodySx: SxProps<Theme> = {
  fontSize: '0.8125rem',
  lineHeight: 1.45,
  color: 'inherit',
};
